import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Input from 'components/Input';
import Select from 'components/Select';
import { SelectProps } from 'utils/types';
import MaskInput from 'components/MaskedInput';
import { unMaskInputValue } from 'utils/helpers';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { formatSwissNumber } from 'utils/formatNumber';

interface Props {
  name: string;
  setName: (value: string) => void;
  area: string;
  setArea: (value: string) => void;
  getErrorOfArea: string | null;
  phAvg?: string | null;
  soilSelect: SelectProps[];
  soilType: string;
  setSoilType: (value: string) => void;
  sec: string;
  initialSec: string;
  setSec: (value: string) => void;
  errorSec: string | null;
  fineDry?: string | null;
  getStatus: string;
  disabled: boolean;
  nameError: boolean;
}

const CropFields: FC<Props> = ({
  name,
  setName,
  area,
  setArea,
  getErrorOfArea,
  phAvg = '0.1',
  soilSelect,
  soilType,
  setSoilType,
  sec,
  initialSec,
  setSec,
  errorSec,
  fineDry = '20',
  getStatus,
  disabled,
  nameError,
}) => {
  const { t } = useTranslation();
  const { kg, onlyAc, isExpertMode } = useCroptimizer();

  return (
    <div>
      <div className="flex mb-6 mx-[-0.5rem]">
        <div className="w-1/3 px-2">
          <Input
            titleClassName={`${
              disabled ? 'bg-transparent' : 'bg-white'
            } top-[-9px] left-[8px] truncate max-w-[85%]`}
            title={t('fieldName')}
            className="w-full"
            value={name}
            onChange={setName}
            disabled={disabled}
            redBorder={nameError && !disabled}
            placeholder={t('enterFieldName')}
            maxLength={30}
          />

          <div className="text-[#FF3B30] text-xs ml-1 mt-1 md:mt-[-15px] md:mb-4 h-2">
            {nameError && !disabled && <div>{t('namefieldunique')}</div>}
          </div>
        </div>
        <div className="w-1/3 px-2">
          <MaskInput
            titleClassName={`${
              disabled ? 'bg-transparent' : 'bg-white'
            } top-[-9px] left-[8px] truncate max-w-[85%]`}
            title={t('surfaceArea') + ` (${onlyAc})`}
            className="w-full"
            value={area}
            onChange={setArea}
            redBorder={!!getErrorOfArea}
            disabled={disabled}
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 md:mt-[-15px] md:mb-4 h-2">
            {getErrorOfArea && <div> {getErrorOfArea}</div>}
          </div>
        </div>
        <div className="w-1/3 px-2">
          <MaskInput
            titleClassName={'bg-disable-gradient top-[-9px] left-[8px] truncate max-w-[85%]'}
            title={t('phavgAnnualDecrease')}
            className="w-full"
            disabled
            value={unMaskInputValue(phAvg ?? '0.1')}
          />
        </div>
      </div>
      <div className="flex md:flex-wrap mx-[-0.5rem] md:space-x-0">
        <div className="flex w-1/3 md:max-w-full md:w-full md:mb-6">
          <div className="w-1/2 px-2">
            <Select
              titleClassName='truncate max-w-[85%]'
              title={t('soilType')}
              options={soilSelect}
              onChange={(value) => setSoilType(value.value)}
              value={
                soilType?.length > 0
                  ? [{ value: soilType,
                    label: soilType }]
                  : []
              }
              disabled={disabled}
            />
          </div>
          {isExpertMode &&
            <div className="w-1/2 px-2">
              <MaskInput
                titleClassName={`top-[-9px] left-[8px] ${
                  disabled ? 'bg-disable-gradient' : ''
                } truncate max-w-[85%]`}
                title={`CEC (Cmol+/${kg})`}
                className="w-full"
                isGreyText={sec === initialSec}
                value={sec}
                onChange={setSec}
                redBorder={!!errorSec}
                disabled={disabled}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 md:mt-[-15px] md:mb-4 h-2">
                {errorSec && <div> {errorSec}</div>}
              </div>
            </div>
          }
        </div>
        <div className="w-2/6 md:w-1/2 md:max-w-full px-2">
          <MaskInput
            titleClassName="bg-disable-gradient top-[-9px] left-[8px] truncate max-w-[85%]"
            title={t('fineDrySoil') + ' (cm)'}
            className="w-full"
            disabled
            value={formatSwissNumber(Number(fineDry ?? '20'))}
          />
        </div>
        <div className="w-2/6 md:w-1/2 md:max-w-full px-2">
          <Input
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
            title={t('status')}
            className="w-full"
            value={getStatus}
            disabled
          />
        </div>
      </div>
    </div>
  );
};

export default CropFields;
